import { getTemplateData } from "frontend/api";
import { fitToStage, waitForFitToNewElements } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { useBoardValue } from "frontend/state/board-state";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { Flags } from "shared/datamodel/schemas";
import { shouldFitWhenPlacingElementRefAtom, layerRefAtom, stageRefAtom, syncServiceAtom } from "state-atoms";
import { posScaleAtom, utilsAtom } from "state-atoms/stage-atoms";
import { on } from "utils/fn-utils";
import { getPathPrefix } from "utils/getPathPrefix";
import { createNewFileMetadata, separateRawDataToElementsAndMetadata } from "utils/template-utils";

export interface TemplateSelectionCardProps {
  id: string;
  name: string;
  icon: string;
  alias: string;
}

export const TEMPLATE_SELECTION_CARDS: TemplateSelectionCardProps[] = [
  {
    id: process.env.CIM_ONBOARDING_FLOWCHART_TEMPLATE_ID ?? "",
    alias: "flowchart",
    name: "Flowchart",
    icon: "/images/onboarding/cim/choose_template/flowchart.svg",
  },
  {
    id: process.env.CIM_ONBOARDING_GANTTCHART_TEMPLATE_ID ?? "",
    alias: "ganttchart",
    name: "Gantt Chart",
    icon: "/images/onboarding/cim/choose_template/ganttchart.svg",
  },
  {
    id: process.env.CIM_ONBOARDING_BRAINSTORM_TEMPLATE_ID ?? "",
    alias: "brainstorm",
    name: "Brainstorm",
    icon: "/images/onboarding/cim/choose_template/brainstorm.svg",
  },
  {
    id: process.env.CIM_ONBOARDING_ORGCHART_TEMPLATE_ID ?? "",
    alias: "orgchart",
    name: "Org Chart",
    icon: "/images/onboarding/cim/choose_template/orgchart.svg",
  },
];

const TEMPLATE_ALIASES = new Map<string, string>(TEMPLATE_SELECTION_CARDS.map((card) => [card.alias, card.id]));

const templateCache = new Map<string, Record<string, unknown>>();

export async function loadTemplate(templateId: string): Promise<Record<string, unknown> | null> {
  const cached = templateCache.get(templateId);
  if (cached) {
    return cached;
  }

  try {
    const template = await getTemplateData(templateId);

    templateCache.set(templateId, template.data);
    return template.data;
  } catch {
    // fallback in case the template is not found in the database
    const response = await fetch(getPathPrefix(`/templates/${TEMPLATE_ALIASES.get(templateId)}.json`));

    if (!response.ok) {
      console.error(`Failed to load template ${templateId}: ${response.status}`);
      return null;
    }

    const templateData = await response.json();
    templateCache.set(templateId, templateData);
    return templateData;
  }
}

export const useLoadTemplates = () => {
  useEffect(() => {
    const loadTemplates = async () => {
      await Promise.all(TEMPLATE_SELECTION_CARDS.map((card) => loadTemplate(card.id)));
    };

    loadTemplates();
  }, []);
};

export const useApplyTemplate = () => {
  const boardValue = useBoardValue() ?? [];
  const board = boardValue?.[0]?.board;
  const syncService = useAtomValue(syncServiceAtom);
  const documentId = board?.documentId;
  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const stageRef = useAtomValue(stageRefAtom);
  const layerRef = useAtomValue(layerRefAtom);
  const [posScale, setPosScale] = useAtom(posScaleAtom);
  const shouldFitWhenPlacingElementRef = useAtomValue(shouldFitWhenPlacingElementRefAtom);

  return useCallback(
    async (templateId: string) => {
      const templateData = await loadTemplate(templateId);

      if (
        !board ||
        !canvasUtils ||
        !stageRef?.current ||
        !layerRef?.current ||
        !templateData ||
        shouldFitWhenPlacingElementRef === null
      ) {
        return;
      }

      const { elements, metadata } = separateRawDataToElementsAndMetadata(templateData);

      shouldFitWhenPlacingElementRef.current = true;

      stageRef.current.opacity(0);

      try {
        if (metadata) {
          const createdMetadata = await createNewFileMetadata(metadata, board.documentId, board.accountId, "File");
          await syncService?.mutate.mergeMetadata(Object.entries(createdMetadata));
        }

        const templateElements = new Map(Object.entries(elements));

        // Elements that are created from template are flagged as system to enable without restriction
        templateElements.forEach((element) => {
          element.flags = on(element.flags, Flags.CreatedBySystem);
        });

        const newElementIds = await canvasUtils.copyElements(templateElements, { x: 0, y: 0 }, true);

        if (newElementIds && newElementIds.length > 0) {
          await waitForFitToNewElements(new Set(newElementIds), layerRef.current, stageRef.current, posScale);
          canvasUtils.setSelectedElementIds(newElementIds);
        }
      } catch (error) {
        console.error("Error applying template:", error);
      }

      stageRef.current.opacity(1);

      shouldFitWhenPlacingElementRef.current = false;

      const newPosScale = fitToStage(stageRef);

      if (newPosScale) {
        setPosScale(newPosScale);
      }
    },
    [canvasUtils, stageRef, layerRef, shouldFitWhenPlacingElementRef, board, syncService?.mutate, posScale, setPosScale]
  );
};
